import { fetchDeals, fetchDealFunnels } from '@/entities/task/api/сrm';
import {
  fetchProjects,
  fetchSmartProcessFunnels,
} from '@/entities/task/api/projects';
import { fetchUsers } from '@/lib/api/users';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildMeetingFunnelOptions } from './funnels';
import { fetchCrmLeads, getMaybeString, userDisplayName } from './helpers';
import { CURRENT_USER_LABEL } from './types';
import type { FunnelOption } from './types';

export function useMeetingFunnels() {
  const smartProcessQuery = useQuery({
    queryKey: ['smart-process-funnels'],
    queryFn: fetchSmartProcessFunnels,
    enabled: true,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const dealFunnelsQuery = useQuery({
    queryKey: ['deal-funnels'],
    queryFn: fetchDealFunnels,
    enabled: true,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const funnelOptions = useMemo<FunnelOption[]>(() => {
    const smartProcessRaw = (smartProcessQuery.data as ANY)?.result ?? [];
    const dealFunnelsRaw = (dealFunnelsQuery.data as ANY)?.result ?? [];
    return buildMeetingFunnelOptions(smartProcessRaw, dealFunnelsRaw);
  }, [dealFunnelsQuery.data, smartProcessQuery.data]);

  return {
    funnelOptions,
    isFetching: smartProcessQuery.isFetching || dealFunnelsQuery.isFetching,
    isError: smartProcessQuery.isError || dealFunnelsQuery.isError,
    error: smartProcessQuery.error ?? dealFunnelsQuery.error,
  };
}

export function useMeetingProjects(
  entityTypeId: number | null,
  debouncedProjectQuery: string
) {
  const query = useQuery({
    queryKey: ['projects-list', entityTypeId, debouncedProjectQuery],
    queryFn: () =>
      fetchProjects({
        entityTypeId: entityTypeId ?? undefined,
        search: debouncedProjectQuery.trim() || undefined,
      } as ANY),
    enabled: !!entityTypeId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const projectItems = (query.data as ANY)?.result ?? [];
  const projectByTitle = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const project of projectItems) {
      const title = getMaybeString(project?.title ?? project?.Title) || '';
      if (title && !map.has(title)) {
        map.set(title, project);
      }
    }
    return map;
  }, [projectItems]);

  const projectOptions = useMemo(() => {
    if (!entityTypeId) {
      return [];
    }
    const titles = (Array.isArray(projectItems) ? projectItems : [])
      .map((project: ANY) => getMaybeString(project?.title ?? project?.Title))
      .filter(Boolean);
    return Array.from(new Set(titles));
  }, [projectItems, entityTypeId]);

  return {
    ...query,
    projectByTitle,
    projectOptions,
  };
}

export function useMeetingDeals(
  enabled: boolean,
  dealCategoryId: number | null,
  debouncedDealQuery: string
) {
  const query = useQuery({
    queryKey: ['meeting-deals', dealCategoryId, debouncedDealQuery],
    queryFn: () =>
      fetchDeals({
        page: 1,
        limit: 100,
        CATEGORY_ID: dealCategoryId ?? undefined,
        title: debouncedDealQuery.trim() || undefined,
      }),
    enabled: enabled && dealCategoryId !== null,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const dealsList = (query.data as ANY)?.result ?? [];
  const dealByOption = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const deal of dealsList) {
      const title =
        getMaybeString(deal?.title ?? deal?.Title) || 'Без названия';
      const id = deal?.id ?? deal?.ID;
      const label = `${title} — #${id}`;
      if (!map.has(label)) {
        map.set(label, deal);
      }
    }
    return map;
  }, [dealsList]);

  const dealOptions = useMemo(
    () => Array.from(dealByOption.keys()),
    [dealByOption]
  );

  return {
    ...query,
    dealByOption,
    dealOptions,
  };
}

export function useMeetingLeads(enabled: boolean, debouncedLeadQuery: string) {
  const query = useQuery({
    queryKey: ['crm-leads', debouncedLeadQuery],
    queryFn: () => fetchCrmLeads(debouncedLeadQuery.trim()),
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const leadsList = (query.data as ANY)?.result ?? [];
  const leadByOption = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const lead of leadsList) {
      const title =
        getMaybeString(lead?.title ?? lead?.Title) || 'Без названия';
      const id = lead?.id ?? lead?.ID;
      const label = `${title} — #${id}`;
      if (!map.has(label)) {
        map.set(label, lead);
      }
    }
    return map;
  }, [leadsList]);

  const leadOptions = useMemo(
    () => Array.from(leadByOption.keys()),
    [leadByOption]
  );

  return {
    ...query,
    leadByOption,
    leadOptions,
  };
}

export function useMeetingUsers(
  presenterOpened: boolean,
  debouncedPresenterQuery: string
) {
  const query = useQuery({
    queryKey: ['users', debouncedPresenterQuery],
    queryFn: () => fetchUsers(debouncedPresenterQuery.trim()),
    enabled: presenterOpened,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const usersList = (query.data as ANY)?.result ?? [];
  const userByLabel = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const user of usersList) {
      const label = userDisplayName(user);
      if (label && !map.has(label)) {
        map.set(label, user);
      }
    }
    return map;
  }, [usersList]);

  const presenterOptions = useMemo(() => {
    const extra = (Array.isArray(usersList) ? usersList : [])
      .map((user: ANY) => userDisplayName(user))
      .filter(Boolean);

    const uniq = Array.from(new Set(extra)).filter(
      (label) => label !== CURRENT_USER_LABEL
    );
    return [CURRENT_USER_LABEL, ...uniq];
  }, [usersList]);

  return {
    ...query,
    presenterOptions,
    userByLabel,
  };
}
